import React, { useState, useEffect, useRef, useCallback, memo } from "react";

export const JsonThemeEditor = memo(({ value = {}, onChange, name, ...props }) => {

    const [localValue, setLocalValue] = useState(""); // Local state for JSON text
    const [, setIsValid] = useState(true); // Validation state
    const textAreaRef = useRef(null); // Reference to the textarea

    // Create a stable reference for value comparison
    const prevValueRef = useRef();

    // Sync `value` prop with localValue, ensuring it's always valid JSON
    useEffect(() => {
        // Deep comparison to prevent infinite re-renders
        const currentValueString = JSON.stringify(value);
        const prevValueString = JSON.stringify(prevValueRef.current);
        
        if (currentValueString !== prevValueString) {
            try {
                const formattedValue =
                    typeof value === "string"
                        ? JSON.stringify(JSON.parse(value), null, 2)
                        : JSON.stringify(value, null, 2);
                setLocalValue(formattedValue);
                setIsValid(true);
            } catch (err) {
                setLocalValue(""); // Clear local value if invalid
                setIsValid(false);
            }
            prevValueRef.current = value;
        }
    }, [value]);

    const handleChange = useCallback((event) => {
        const updatedValue = event.target.value;
        setLocalValue(updatedValue);

        try {
            const parsed = JSON.parse(updatedValue);
            setIsValid(true);
            // Trigger parent onChange with parsed JSON
            onChange({ target: { name, value: parsed } });
        } catch (err) {
            setIsValid(false); // Mark invalid if JSON parsing fails
        }
    }, [onChange, name]);

    const countLines = useCallback(() => {
        return localValue.split("\n").length;
    }, [localValue]);

    const renderLineNumbers = useCallback(() => {
        const lines = countLines();
        return Array.from({ length: lines }, (_, i) => (
            <div key={i} style={{ textAlign: "right", paddingRight: "10px" }}>
                {i + 1}
            </div>
        ));
    }, [countLines]);

    return (
        <div style={{ display: "flex", border: "1px solid #ddd", borderRadius: "4px" }}>
            {/* Line numbers */}
            <div
                style={{
                    background: "#f5f5f5",
                    padding: "10px 0",
                    fontFamily: "monospace",
                    fontSize: "14px",
                    color: "#999",
                    borderRight: "1px solid #ddd",
                }}
            >
                {renderLineNumbers()}
            </div>

            {/* Textarea for JSON editing */}
            <textarea
                ref={textAreaRef}
                value={localValue}
                onChange={handleChange}
                rows={20}
                style={{
                    flex: 1,
                    fontFamily: "monospace",
                    fontSize: "14px",
                    border: "none",
                    padding: "10px",
                    outline: "none",
                    resize: "none",
                }}
                {...props}
            />
        </div>
    );
});
